/**
 * Generic SVG rendering of a cluster model. Deliberately NOT a replica of any carmaker's dial:
 * neutral faces, neutral type, our own needle. Needles are animated with the same damping
 * function the firmware is expected to use.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  dialToAngle, multiplierLabel, stepNeedle, ticks,
  type ClusterModel, type LampSymbol, type NeedleGauge, type SetView, type GaugeView, type NeedleSettings, type OrgSettings, type NeedleState,
} from '@dg/core';

interface Props {
  model: ClusterModel;
  view: SetView;
  settingsFor: (gaugeId: string) => NeedleSettings | undefined;
  colors: OrgSettings['colors'];
  selected: string | null;
  onSelect: (gaugeId: string) => void;
}

export function ClusterPreview({ model, view, settingsFor, colors, selected, onSelect }: Props) {
  const { t } = useTranslation();
  const { width: W, height: H } = model.canvas;
  const needles = useRef(new Map<string, SVGGElement>());
  const state = useRef(new Map<string, NeedleState>());
  const targets = useRef(new Map<string, { dial: number; damping: number; gauge: NeedleGauge }>());

  // Keep animation targets in a ref so the loop never restarts on re-render.
  for (const g of model.gauges) {
    const v = view.gauges[g.id];
    if (g.type === 'needle' && v?.dial !== undefined) targets.current.set(g.id, { dial: v.dial, damping: v.dampingMs ?? 0, gauge: g });
  }

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let last = performance.now();
    const frame = (now: number) => {
      const dt = Math.min(now - last, 100);
      last = now;
      for (const [id, tg] of targets.current) {
        const prev = state.current.get(id) ?? { pos: tg.gauge.min, vel: 0 };
        const next = reduce ? { pos: tg.dial, vel: 0 } : stepNeedle(prev, tg.dial, dt, Math.max(tg.damping, 120));
        state.current.set(id, next);
        needles.current.get(id)?.setAttribute('transform', `rotate(${dialToAngle(next.pos, tg.gauge)})`);
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <svg className="cluster" viewBox={`0 0 ${W} ${H}`} role="group" aria-label={model.label}>
      <defs>
        <linearGradient id="bezel" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e3e6e9" /><stop offset="0.3" stopColor="#7c838b" /><stop offset="0.55" stopColor="#cfd3d7" /><stop offset="1" stopColor="#5d646c" />
        </linearGradient>
        <radialGradient id="face" cx="50%" cy="40%" r="65%">
          <stop offset="0" stopColor="#1a1d22" /><stop offset="1" stopColor="#07080a" />
        </radialGradient>
        <filter id="glow"><feGaussianBlur stdDeviation="2.5" /></filter>
      </defs>
      <rect x="0" y="0" width={W} height={H} rx="10" fill="#08090b" stroke="#23272d" />
      {model.gauges.map((g) => {
        const cx = (g.layout.x / 100) * W;
        const cy = (g.layout.y / 100) * H;
        const r = ((g.layout.size / 100) * W) / 2;
        const v = view.gauges[g.id]!;
        const isSel = selected === g.id;
        const common = {
          transform: `translate(${cx} ${cy}) scale(${r / 100})`,
          className: `gauge-hit${isSel ? ' is-selected' : ''}`,
          role: 'button',
          tabIndex: 0,
          'aria-pressed': isSel,
          'aria-label': `${g.label}: ${describe(v, t)}`,
          onClick: () => onSelect(g.id),
          onKeyDown: (e: React.KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect(g.id)),
        };
        if (g.type === 'needle') return <g key={g.id} {...common}><NeedleDial gauge={g} view={v} settings={settingsFor(g.id)} colors={colors} selected={isSel} needleRef={(el) => el && needles.current.set(g.id, el)} t={t} /></g>;
        if (g.type === 'segment_display')
          return (
            <g key={g.id} {...common}>
              <rect x="-100" y="-40" width="200" height="80" rx="8" fill="#0d1a12" stroke={isSel ? 'var(--red-text)' : '#2a2f37'} strokeWidth="4" />
              <text x="0" y="18" textAnchor="middle" fontFamily="var(--f-mono)" fontSize="52" fill="#b8f5c8">{v.text || '····'}</text>
            </g>
          );
        const lit = !!v.light;
        return (
          <g key={g.id} {...common}>
            <title>{g.label}</title>
            <circle r="92" fill="#111317" stroke={isSel ? 'var(--red-text)' : '#2a2f37'} strokeWidth={isSel ? 10 : 5} />
            <g className={v.light === 2 ? 'blink' : undefined} color={lit ? lampColor(g.color) : '#3b4048'}>
              {lit && <g filter="url(#glow)" opacity="0.8"><LampIcon symbol={g.symbol} /></g>}
              <LampIcon symbol={g.symbol} />
            </g>
          </g>
        );
      })}
    </svg>
  );
}

function NeedleDial({ gauge, view, settings, colors, selected, needleRef, t }: { gauge: NeedleGauge; view: GaugeView; settings?: NeedleSettings; colors: OrgSettings['colors']; selected: boolean; needleRef: (el: SVGGElement | null) => void; t: (k: string) => string }) {
  const pt = (angle: number, radius: number) => {
    const rad = (angle * Math.PI) / 180;
    return [radius * Math.sin(rad), -radius * Math.cos(rad)] as const;
  };
  const arc = (from: number, to: number, radius: number) => {
    const a0 = dialToAngle(from, gauge);
    const a1 = dialToAngle(to, gauge);
    const [x0, y0] = pt(a0, radius);
    const [x1, y1] = pt(a1, radius);
    return `M${x0} ${y0}A${radius} ${radius} 0 ${Math.abs(a1 - a0) > 180 ? 1 : 0} 1 ${x1} ${y1}`;
  };
  // Assigned: relabel ticks with KPI values. Unassigned: show the original printed scale.
  const tickList = ticks(gauge, settings ? { kpi_min: settings.kpi_min, kpi_max: settings.kpi_max, multiplier: settings.multiplier } : { kpi_min: gauge.min, kpi_max: gauge.max, multiplier: 1 });
  // Dense scales (e.g. 0–260 in 20s): label every other major tick so numbers never collide.
  const majors = tickList.filter((tk) => tk.major);
  const labelEvery = majors.length > 10 ? 2 : 1;
  const labelled = new Set(majors.filter((_, i) => i % labelEvery === 0).map((tk) => tk.dial));
  const ring = view.state === 'none' ? '#3a414b' : view.state === 'stale' ? colors.stale : colors[view.state];
  const redFrom = view.redlineDial ?? (settings ? undefined : gauge.printed_redline);
  const readout = view.value === null ? '—' : formatValue(view.value);

  return (
    <>
      <circle r="100" fill="url(#bezel)" />
      <circle r="94" fill="url(#face)" />
      <circle r="96.5" fill="none" stroke={selected ? 'var(--red-text)' : ring} strokeWidth={selected ? 4 : 2.5} opacity={view.state === 'none' && !selected ? 0.5 : 1} />
      {redFrom !== undefined && <path d={arc(redFrom, gauge.max, 84)} stroke="#e3242b" strokeWidth="7" fill="none" />}
      {tickList.map((tk) => {
        const a = dialToAngle(tk.dial, gauge);
        const [x0, y0] = pt(a, 88);
        const [x1, y1] = pt(a, tk.major ? 74 : 81);
        const [lx, ly] = pt(a, 60);
        return (
          <g key={tk.dial}>
            <path d={`M${x0} ${y0}L${x1} ${y1}`} stroke={redFrom !== undefined && tk.dial >= redFrom ? '#ff5a5f' : '#e9ebee'} strokeWidth={tk.major ? 2.4 : 1.2} />
            {tk.label !== null && labelled.has(tk.dial) && (
              <text x={lx} y={ly + 4.5} textAnchor="middle" fontSize={tk.label.length > 3 ? 9 : 12} fontFamily="var(--f-display)" fontWeight="600" fill="#eceef1">{tk.label}</text>
            )}
          </g>
        );
      })}
      <text y="30" textAnchor="middle" fontSize="8.5" fontFamily="var(--f-mono)" letterSpacing="1" fill="#a9b0b9">
        {(settings ? settings.unit_label || '' : gauge.unit).toUpperCase().slice(0, 22)}
      </text>
      {settings && settings.multiplier !== 1 && <text y="41" textAnchor="middle" fontSize="7.5" fontFamily="var(--f-mono)" fill="#858d98">{multiplierLabel(settings.multiplier)}</text>}
      <text y="60" textAnchor="middle" fontSize="13" fontFamily="var(--f-mono)" fill={view.stale ? colors.stale : '#eceef1'}>
        {settings ? (view.stale ? t('configurator.stale').toUpperCase() : readout) : t('configurator.unassigned')}
      </text>
      {view.alert && <path d="M0 64 L9 79 H-9 Z" fill={colors.alert} filter="url(#glow)" transform="translate(0 4)" />}
      <g ref={needleRef} opacity={view.stale ? 0.4 : 1}>
        <path d="M-2.6 18 L-1.1 -84 L1.1 -84 L2.6 18 Z" fill="#ff4a2e" />
      </g>
      <circle r="9" fill="#50565e" stroke="#0b0c0e" strokeWidth="1.5" />
    </>
  );
}

/**
 * Generic line icons for warning lamps, drawn in a ±60 box with currentColor.
 * Our own simple shapes: they only hint at the lamp, never copy a carmaker's artwork.
 */
function LampIcon({ symbol }: { symbol: LampSymbol }) {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 9, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
  const f = { fill: 'currentColor' } as const;
  switch (symbol) {
    case 'alert':
      return <g {...p}><path d="M0 -52 L55 44 H-55 Z" /><path d="M0 -18 V12" /><circle cy="28" r="2" /></g>;
    case 'info':
      return <g {...p}><circle r="50" /><path d="M0 -4 V30" /><circle cy="-24" r="2" /></g>;
    case 'check':
      return <g {...p}><path d="M-40 0 L-12 28 L42 -30" /></g>;
    case 'fuel':
      return <g {...p}><rect x="-38" y="-45" width="46" height="90" rx="6" /><path d="M-30 -20 H0" /><path d="M8 -20 H22 V30 Q22 40 32 40 Q42 40 42 30 V-18 L30 -34" /></g>;
    case 'temperature':
      return <g {...p}><path d="M-8 -50 V14" /><circle cy="30" r="16" /><path d="M-8 -34 H10 M-8 -16 H10 M-8 2 H10" /><path d="M-52 52 Q-34 42 -17 52 Q0 62 17 52 Q34 42 52 52" /></g>;
    case 'battery':
      return <g {...p}><rect x="-52" y="-30" width="104" height="66" rx="6" /><path d="M-34 -30 V-42 M34 -30 V-42" /><path d="M-36 4 H-14 M-25 -7 V15 M14 4 H36" /></g>;
    case 'oil':
      return <g {...p}><path d="M-55 10 H10 L45 -12 L55 -4" /><path d="M-55 10 V34 H22 L45 -12" /><path d="M-40 10 V-6 H-6 V10" /><path d="M56 16 Q50 28 56 34 Q62 28 56 16 Z" {...f} /></g>;
    case 'engine':
      return <g {...p}><path d="M-44 -18 H24 L36 -6 H48 V26 H36 L24 38 H-30 L-44 24 Z" /><path d="M-56 -4 V16 M-20 -18 V-34 M-34 -34 H-4" /></g>;
    case 'beam':
      return <g {...p}><path d="M-6 -36 Q46 -36 46 0 Q46 36 -6 36 Z" /><path d="M-54 -26 H-22 M-54 -9 H-22 M-54 9 H-22 M-54 26 H-22" /></g>;
    case 'arrow_left':
      return <g {...f}><path d="M-55 0 L-5 -45 V-20 H52 V20 H-5 V45 Z" /></g>;
    case 'arrow_right':
      return <g {...f}><path d="M55 0 L5 -45 V-20 H-52 V20 H5 V45 Z" /></g>;
    case 'washer_fluid':
      return <g {...p}><path d="M-52 28 Q0 -26 52 28 Z" /><path d="M0 22 V2 M-24 -2 L-38 -34 M0 -8 V-50 M24 -2 L38 -34" strokeDasharray="10 9" /></g>;
    case 'brake':
      return <g {...p}><circle r="36" /><path d="M-50 -30 Q-64 0 -50 30 M50 -30 Q64 0 50 30" /><path d="M0 -20 V6" /><circle cy="20" r="2" /></g>;
    case 'tyre_pressure':
      return <g {...p}><path d="M-38 -42 Q-54 -10 -44 20 L-38 42 H38 L44 20 Q54 -10 38 -42" /><path d="M-40 42 V52 M-20 42 V52 M0 42 V52 M20 42 V52 M40 42 V52" /><path d="M0 -24 V4" /><circle cy="20" r="2" /></g>;
    case 'door':
      return <g {...p}><path d="M-40 50 V-20 L-8 -50 H40 V50 Z" /><path d="M-40 -8 H40" /><path d="M16 12 H28" /></g>;
    case 'seatbelt':
      return <g {...p}><circle cy="-38" r="12" /><path d="M-22 50 V-2 Q-22 -18 -6 -18 H6 Q22 -18 22 -2 V50" /><path d="M-20 -14 L22 36" /></g>;
    case 'service':
      return <g {...p}><path d="M34 -52 A24 24 0 0 0 10 -20 L-44 34 A10 10 0 0 0 -30 48 L24 -6 A24 24 0 0 0 54 -30 L38 -16 L22 -32 Z" /></g>;
    default:
      return <g {...p}><circle r="40" /><circle r="10" {...f} /></g>;
  }
}

function lampColor(c: string) {
  return { red: '#e3242b', amber: '#f2a93b', green: '#1faa59', blue: '#3d7bff', white: '#f4f5f7' }[c] ?? '#f2a93b';
}

export function formatValue(v: number) {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e4) return `${(v / 1e3).toFixed(1)}k`;
  if (abs >= 100) return Math.round(v).toLocaleString('en');
  return (Math.round(v * 100) / 100).toString();
}

function describe(v: GaugeView, t: (k: string) => string) {
  if (!v.assigned) return t('configurator.unassigned');
  if (v.stale) return t('configurator.stale');
  return `${v.value === null ? '—' : formatValue(v.value)}${v.alert ? `, ${t('configurator.alert')}` : ''}`;
}
